import { AxiosInstance, AxiosResponse } from 'axios';
import type {
  ApiResponse,
  AssociatedRouteResponse,
  ContactResponse,
  ContactTagResponse,
  CreateContactRequest,
  CreateLocationRequest,
  CreateNoteRequest,
  CreatePositionRequest,
  DriverSuggestionResponse,
  LocationFileResponse,
  LocationListResponse,
  LocationResponse,
  NoteResponse,
  PositionResponse,
  UpdateContactRequest,
  UpdateLocationRequest,
  UpdateNoteRequest,
  UpdatePositionRequest,
} from '../types/locationApi';
import type {
  AssetListResponse,
  AssetResponse,
  CreateAssetRequest,
  UpdateAssetRequest,
} from '../types/assetApi';
import type {
  RouteDistanceCalculationResponse,
  TemplateDistanceCalculationResponse,
} from '../services/distanceService';
import type { GeocodingResponse } from '../utils/graphhopperUtils';

enum GenerateFixedTourStatus {
  Success,
  Failed,
}

interface GenerateFixedTourResponse {
  status: GenerateFixedTourStatus;
  error?: string;
  stats?: ({
    generatedFrom: Date;
    generatedTo: Date;
    newRoutesCreated: number;
  } | null)[];
}

interface GenerateRouteShareLinkResponse {
  success: boolean;
  data: {
    id: string;
    routeId: number;
    expiresAt: string;
    url: string;
    createdAt: string;
  };
}

const PAPPV2 = '/pappv2/api';
const LOCATION_API = '/api/locations/v1';
//const QA_ORDERS_API = 'https://nexus.qa.bring.services/api/orders';
const DISTANCE_API = `${LOCATION_API}/distance`;
// Assets are served by the same nexus-location-service, under the shared
// `/api/locations` context path (controller mapping is `/v1/assets`).
const ASSET_API = '/api/locations/v1/assets';
const ORDER_API = '/api/orders';
const CUSTOMS_HUB_API = '/api/customs/customs-hub';

export class HttpClient {
  constructor(private readonly client: AxiosInstance) {}

  async uploadCardFile(cardId: number, file: File): Promise<void> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return await this.client.post(`${PAPPV2}/card/${cardId}/file`, formData);
  }

  async uploadDocumentFile(
    file: File,
    tagIds: number[],
    signal?: AbortSignal,
  ): Promise<void> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    tagIds.forEach((tagId) => {
      formData.append('tagIds[]', `${tagId}`);
    });

    return await this.client.post(`${PAPPV2}/files`, formData, {
      signal,
    });
  }

  downloadFile<T extends 'blob' | 'arraybuffer'>(
    file: { path: string },
    responseType: T,
  ): Promise<AxiosResponse<T extends 'blob' ? Blob : ArrayBuffer>> {
    const path = escape(file.path);
    return this.client.get(`${PAPPV2}/files?path=${path}`, {
      responseType,
    });
  }

  async uploadAttachmentFile(
    file: File,
    entityId: number,
    entityType: string,
  ): Promise<void> {
    const formData = new FormData();

    formData.append('file', file, file.name);
    formData.append('entityId', `${entityId}`);
    formData.append('entityType', entityType);

    return await this.client.post(`${PAPPV2}/files/attachmentfile`, formData);
  }

  async uploadLegImages(
    files: File[],
    legId: number,
  ): Promise<
    AxiosResponse<{
      status: 'success';
      files: Pick<
        import('../generated/graphql').DocumentFile,
        'id' | 'size' | 'path' | 'mimetype' | 'originalname'
      >[];
    }>
  > {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file, file.name));
    return this.client.post(`${PAPPV2}/files/legs/${legId}/images`, formData, {
      responseType: 'json',
    });
  }

  // downloadEndReportBlob(data: {
  //   isDownloadAsFile: boolean;
  //   internalTerminalId: number;
  //   productionDate: string;
  //   startDateTime: string;
  //   endDateTime: string;
  //   userFullName: string;
  //   locationId: number;
  // }): Promise<AxiosResponse<Blob>> {
  //   return this.client.post(`${PAPPV2}/endreport`, data, {
  //     responseType: 'blob',
  //   });
  // }

  downloadTftExcelBlob(data: {
    locationId: number[];
    startDateTime: string;
    endDateTime: string;
  }): Promise<AxiosResponse<Blob>> {
    return this.client.post(`${PAPPV2}/endreport/tft-excel`, data, {
      responseType: 'blob',
    });
  }

  downloadImportTemplateBlob(data: {
    cardType: string;
  }): Promise<AxiosResponse<Blob>> {
    return this.client.post(`${PAPPV2}/endreport/import-template`, data, {
      responseType: 'blob',
    });
  }

  async uploadReportFile(file: File): Promise<void> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return await this.client.post(`${PAPPV2}/files/report`, formData);
  }

  async uploadRouteFile(
    file: File,
    routeId: number,
    signal?: AbortSignal,
  ): Promise<void> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('routeId', `${routeId}`);
    return await this.client.post(`${PAPPV2}/files/route`, formData, {
      signal,
    });
  }

  downloadFileArrayBuffer(file: {
    path: string;
  }): Promise<AxiosResponse<ArrayBuffer>> {
    const path = escape(file.path);
    return this.client.get(`${PAPPV2}/files?path=${path}`, {
      responseType: 'arraybuffer',
    });
  }

  downloadCMRBlob(data: {
    senderLocationId?: number;
    receiverLocationId?: number;
    subcontractorId?: number;
    subcontractorTwoId?: number;
    deliveryDate?: string;
  }): Promise<AxiosResponse<Blob>> {
    return this.client.post(`${PAPPV2}/cmr`, data, {
      responseType: 'blob',
    });
  }

  downloadCMRBlobFromTFT(data: {
    legId: number;
  }): Promise<AxiosResponse<Blob>> {
    return this.client.post(`${PAPPV2}/routes/get-cmr`, data, {
      responseType: 'blob',
    });
  }

  downloadLoadingListBlob(data: {
    routeId: number;
  }): Promise<AxiosResponse<Blob>> {
    return this.client.post(`${PAPPV2}/loadingList`, data, {
      responseType: 'blob',
    });
  }

  // downloadPeakPlanningList(data: {
  //   startDate: string;
  //   endDate: string;
  // }): Promise<AxiosResponse<Blob>> {
  //   return this.client.post(`${PAPPV2}/peakPlanningList`, data, {
  //     responseType: 'blob',
  //   });
  // }
  // downloadPeakPlanningDebitList(data: {
  //   startDate: string;
  //   endDate: string;
  // }): Promise<AxiosResponse<Blob>> {
  //   return this.client.post(`${PAPPV2}/debitList`, data, {
  //     responseType: 'blob',
  //   });
  // }

  // downloadKPITerminal(data: {
  //   terminalId: number;
  //   date: string;
  // }): Promise<AxiosResponse<Blob>> {
  //   return this.client.post(`${PAPPV2}/kpi/terminal`, data, {
  //     responseType: 'blob',
  //   });
  // }

  generateFixedRoutesByDateRange(input: {
    skipCronCheck: boolean;
    skipDBEntry: boolean;
    dateRange: {
      startDate: string;
      endDate?: string;
    };
  }): Promise<AxiosResponse<GenerateFixedTourResponse>> {
    return this.client.post(
      `${PAPPV2}/fixed-tour/generate-by-date-range`,
      input,
      {
        responseType: 'json',
      },
    );
  }

  generateRoutesFromTemplate(
    tourId: number,
    routesData?: any,
  ): Promise<AxiosResponse<GenerateFixedTourResponse>> {
    return this.client.post(
      `${PAPPV2}/fixed-tour/generate-from-template/${tourId}`,
      routesData || {},
      {
        responseType: 'json',
      },
    );
  }

  downloadTourSchedule(tourId: number) {
    return this.client.get(`${PAPPV2}/tourTemplate/schedule/${tourId}`, {
      responseType: 'blob',
    });
  }

  getIncomingRouteLegs(input: {
    locationId: number[];
    startDateTime: string;
    endDateTime: string;
    routeFilterType?: string;
    sporadicOnly?: boolean;
    palletSum?: boolean;
    allCancelledLegs?: boolean;
    fromTftReadyOnly?: boolean;
  }): Promise<AxiosResponse<any>> {
    return this.client.post(`${PAPPV2}/routes/incomingroutes`, input, {
      responseType: 'json',
    });
  }

  getLegsList(input: {
    startDateTime: string;
    endDateTime: string;
    locationIds: number[];
    filterModel?: any;
    customSearch?: boolean;
  }): Promise<AxiosResponse<any>> {
    return this.client.post(`${PAPPV2}/routes/legslist`, input, {
      responseType: 'json',
    });
  }

  getListRoutes(input: {
    startDate: string;
    endDate: string;
    isFromCustom: boolean;
    filterConfig: string;
  }): Promise<AxiosResponse<any>> {
    return this.client.post(`${PAPPV2}/routes/listroutes`, input, {
      responseType: 'json',
    });
  }

  getDispatchRoutes(input: {
    startDate: string;
    endDate: string;
    isFromCustom: boolean;
    filterConfig: string;
  }): Promise<AxiosResponse<any>> {
    return this.client.post(`${PAPPV2}/routes/dispatch-routes`, input, {
      responseType: 'json',
    });
  }

  getPlannedRoutes(input: {
    createdStartDate: string;
    createdEndDate: string;
  }): Promise<AxiosResponse<any>> {
    return this.client.post(`${PAPPV2}/routes/plannedroutes`, input, {
      responseType: 'json',
    });
  }

  getAllTourTemplates(): Promise<AxiosResponse<any>> {
    return this.client.post(`${PAPPV2}/tourTemplate/all`, {
      responseType: 'json',
    });
  }

  getAllTourRoutes(): Promise<AxiosResponse<any>> {
    return this.client.post(`${PAPPV2}/tourTemplate/tourRoutes/all`, {
      responseType: 'json',
    });
  }

  getBookmarkedRoutes(input: { date: string }): Promise<AxiosResponse<any>> {
    return this.client.post(`${PAPPV2}/tourTemplate/bookmarked-routes`, input, {
      responseType: 'json',
    });
  }

  generateRouteShareLink(input: {
    routeId: number;
    ttlHours?: number;
  }): Promise<AxiosResponse<GenerateRouteShareLinkResponse>> {
    return this.client.post(`${PAPPV2}/routes/share-links`, input, {
      responseType: 'json',
    });
  }

  getCheckListRoutes(input: {
    transportationDate: string;
    routeGroupTag: string;
  }): Promise<AxiosResponse<any>> {
    return this.client.post(`${PAPPV2}/routes/checklist-routes`, input, {
      responseType: 'json',
    });
  }

  getRouteLegsForAllTerminal(input: {
    startDateTime: string;
    endDateTime: string;
  }): Promise<AxiosResponse<any>> {
    return this.client.post(`${PAPPV2}/routes/all-terminal-routes`, input, {
      responseType: 'json',
    });
  }

  getOrdersForShipment(input: {
    startDate: string;
    endDate: string;
    isOrderHidden: boolean;
    createdFrom?: string;
  }): Promise<AxiosResponse<any>> {
    return this.client.get(ORDER_API, {
      params: input,
      responseType: 'json',
    });
  }

  getOrderEdiByWaybill(waybillNumber: string): Promise<AxiosResponse<any>> {
    return this.client.get(
      `${ORDER_API}/edi/${encodeURIComponent(waybillNumber)}`,
      { responseType: 'json' },
    );
  }

  deleteOrdersForShipment(input: { id: number }): Promise<AxiosResponse<void>> {
    return this.client.delete<void>(`${ORDER_API}/${input.id}`);
  }

  /* ------------------------------------------------------------------ */
  /*  Location Microservice (nexus-location-service)                     */
  /* ------------------------------------------------------------------ */

  getLocations(params?: {
    search?: string;
    country?: string;
  }): Promise<AxiosResponse<ApiResponse<LocationListResponse[]>>> {
    return this.client.get(LOCATION_API, { params, responseType: 'json' });
  }

  getLocation(
    id: number,
  ): Promise<AxiosResponse<ApiResponse<LocationResponse>>> {
    return this.client.get(`${LOCATION_API}/${id}`, { responseType: 'json' });
  }

  createLocation(
    data: CreateLocationRequest,
  ): Promise<AxiosResponse<ApiResponse<LocationResponse>>> {
    return this.client.post(LOCATION_API, data, { responseType: 'json' });
  }

  updateLocation(
    id: number,
    data: UpdateLocationRequest,
  ): Promise<AxiosResponse<ApiResponse<LocationResponse>>> {
    return this.client.put(`${LOCATION_API}/${id}`, data, {
      responseType: 'json',
    });
  }

  deleteLocation(id: number): Promise<AxiosResponse<ApiResponse<void>>> {
    return this.client.delete(`${LOCATION_API}/${id}`, {
      responseType: 'json',
    });
  }

  getAssociatedRoutes(
    locationId: number,
  ): Promise<AxiosResponse<ApiResponse<AssociatedRouteResponse[]>>> {
    return this.client.get(`${LOCATION_API}/${locationId}/associated-routes`, {
      responseType: 'json',
    });
  }

  /** Forward geocoding proxied by nexus-location-service (keeps the GraphHopper key server-side). */
  geocodeSearch(
    search: string,
    signal?: AbortSignal,
  ): Promise<AxiosResponse<GeocodingResponse>> {
    return this.client.get(`${LOCATION_API}/geocode`, {
      params: { search },
      responseType: 'json',
      signal,
    });
  }

  /** Reverse geocoding proxied by nexus-location-service. */
  reverseGeocode(
    lat: number,
    lng: number,
    signal?: AbortSignal,
  ): Promise<AxiosResponse<GeocodingResponse>> {
    return this.client.get(`${LOCATION_API}/reverse-geocode`, {
      params: { latitude: lat, longitude: lng },
      responseType: 'json',
      signal,
    });
  }

  getDriverSuggestions(
    search: string,
  ): Promise<AxiosResponse<ApiResponse<DriverSuggestionResponse[]>>> {
    return this.client.get(`${LOCATION_API}/drivers`, {
      params: { search, limit: 5 },
      responseType: 'json',
    });
  }

  calculateTemplateDistance(
    templateId: number,
  ): Promise<AxiosResponse<TemplateDistanceCalculationResponse>> {
    return this.client.post(
      `${DISTANCE_API}/templates/${templateId}/calculate`,
      undefined,
      {
        responseType: 'json',
      },
    );
  }

  calculateRouteDistance(
    routeId: number,
  ): Promise<AxiosResponse<RouteDistanceCalculationResponse>> {
    return this.client.post(
      `${DISTANCE_API}/routes/${routeId}/calculate`,
      undefined,
      {
        responseType: 'json',
      },
    );
  }

  createContact(
    locationId: number,
    data: CreateContactRequest,
  ): Promise<AxiosResponse<ApiResponse<ContactResponse>>> {
    return this.client.post(
      `${LOCATION_API}/locations/${locationId}/contacts`,
      data,
      { responseType: 'json' },
    );
  }

  getContactTags(): Promise<AxiosResponse<ApiResponse<ContactTagResponse[]>>> {
    return this.client.get(`${LOCATION_API}/contact-tags/search`, {
      params: { query: '' },
      responseType: 'json',
    });
  }

  updateContact(
    id: number,
    data: UpdateContactRequest,
  ): Promise<AxiosResponse<ApiResponse<ContactResponse>>> {
    return this.client.put(`${LOCATION_API}/contacts/${id}`, data, {
      responseType: 'json',
    });
  }

  deleteContact(id: number): Promise<AxiosResponse<ApiResponse<void>>> {
    return this.client.delete(`${LOCATION_API}/contacts/${id}`, {
      responseType: 'json',
    });
  }

  createNote(
    locationId: number,
    data: CreateNoteRequest,
  ): Promise<AxiosResponse<ApiResponse<NoteResponse>>> {
    return this.client.post(
      `${LOCATION_API}/locations/${locationId}/notes`,
      data,
      { responseType: 'json' },
    );
  }

  updateNote(
    id: number,
    data: UpdateNoteRequest,
  ): Promise<AxiosResponse<ApiResponse<NoteResponse>>> {
    return this.client.put(`${LOCATION_API}/notes/${id}`, data, {
      responseType: 'json',
    });
  }

  deleteNote(id: number): Promise<AxiosResponse<ApiResponse<void>>> {
    return this.client.delete(`${LOCATION_API}/notes/${id}`, {
      responseType: 'json',
    });
  }

  getLocationFiles(
    locationId: number,
  ): Promise<AxiosResponse<ApiResponse<LocationFileResponse[]>>> {
    return this.client.get(`${LOCATION_API}/${locationId}/files`, {
      responseType: 'json',
    });
  }

  uploadLocationFile(
    locationId: number,
    file: File,
  ): Promise<AxiosResponse<ApiResponse<LocationFileResponse>>> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.client.post(`${LOCATION_API}/${locationId}/files`, formData);
  }

  downloadLocationFile(fileId: number): Promise<AxiosResponse<Blob>> {
    return this.client.get(`${LOCATION_API}/files/${fileId}/download`, {
      responseType: 'blob',
    });
  }

  deleteLocationFile(
    fileId: number,
  ): Promise<AxiosResponse<ApiResponse<void>>> {
    return this.client.delete(`${LOCATION_API}/files/${fileId}`, {
      responseType: 'json',
    });
  }

  /* ---- Positions ---- */

  getPositions(
    locationId: number,
  ): Promise<AxiosResponse<ApiResponse<PositionResponse[]>>> {
    return this.client.get(`${LOCATION_API}/positions/${locationId}`, {
      responseType: 'json',
    });
  }

  createPosition(
    locationId: number,
    data: CreatePositionRequest,
  ): Promise<AxiosResponse<ApiResponse<PositionResponse>>> {
    return this.client.post(`${LOCATION_API}/positions/${locationId}`, data, {
      responseType: 'json',
    });
  }

  updatePosition(
    id: number,
    data: UpdatePositionRequest,
  ): Promise<AxiosResponse<ApiResponse<PositionResponse>>> {
    return this.client.put(`${LOCATION_API}/positions/${id}`, data, {
      responseType: 'json',
    });
  }

  deletePosition(id: number): Promise<AxiosResponse<ApiResponse<void>>> {
    return this.client.delete(`${LOCATION_API}/positions/${id}`, {
      responseType: 'json',
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Asset Microservice (nexus-location-service)                        */
  /* ------------------------------------------------------------------ */

  getAssets(params?: {
    search?: string;
  }): Promise<AxiosResponse<ApiResponse<AssetListResponse[]>>> {
    return this.client.get(ASSET_API, { params, responseType: 'json' });
  }

  getAsset(id: number): Promise<AxiosResponse<ApiResponse<AssetResponse>>> {
    return this.client.get(`${ASSET_API}/${id}`, { responseType: 'json' });
  }

  createAsset(
    data: CreateAssetRequest,
  ): Promise<AxiosResponse<ApiResponse<AssetResponse>>> {
    return this.client.post(ASSET_API, data, { responseType: 'json' });
  }

  updateAsset(
    id: number,
    data: UpdateAssetRequest,
  ): Promise<AxiosResponse<ApiResponse<AssetResponse>>> {
    return this.client.put(`${ASSET_API}/${id}`, data, {
      responseType: 'json',
    });
  }

  deleteAsset(id: number): Promise<AxiosResponse<ApiResponse<void>>> {
    return this.client.delete(`${ASSET_API}/${id}`, {
      responseType: 'json',
    });
  }

  /* ---- Customs-hub ---- */
  sendJsonToCustomsHub(
    tripID: number,
  ): Promise<AxiosResponse<ApiResponse<void>>> {
    return this.client.post(`${CUSTOMS_HUB_API}/upload/${tripID}`, null, {
      responseType: 'json',
    });
  }
}
